/**
 * Application Entry Point
 * (TS Migrated)
 */

import React from 'react';
import ReactDOM from 'react-dom/client';
import './styles/index.css';
import './styles/print.css';
import App from './App';
import reportWebVitals from './reportWebVitals';
import { postWebVital, emitFrontendEvent, initAdTracking, readPersistedOptOut } from './telemetry';
import { initBrowserErrorTelemetry } from './browserErrorTelemetry';
import { LocalStorageKey } from './stores/globalStore';
import { initializeSentry } from './sentryTelemetry';

// --- Build marker (deploy-visibility diagnostic) -------------------------------
// Set at BUILD TIME from the human-readable release version (REACT_APP_BUILD_ID, injected as
// a Docker build-arg by live_deploy_node.sh / docker-compose.yml's frontend
// service) so a browser can UNAMBIGUOUSLY report which build it loaded — the
// shipped UI features are conditional/subtle, so their absence is a poor signal
// for "did my deploy reach me?". Check in DevTools console: read the logged
// line, or type `__MSAPLING_BUILD__`. If this tag matches the latest deploy's
// V1.xx tag, the new bundle IS loaded (any missing feature is then visibility, not
// deploy); if it's stale/absent, the browser/CDN is serving an old build.
//
// HISTORY: this used to be a hand-bumped literal (`BUILD_TAG = 'reconcile-
// 2026-07-07'`) that the comment asked engineers to bump "on each deploy" — it
// was bumped exactly once, then sat stale through ~48 subsequent commits that
// touched this file, silently defeating its entire purpose. Deriving it from
// the actual build-arg removes the manual step so it cannot go stale again.
const BUILD_TAG = process.env.REACT_APP_BUILD_ID || 'dev-local';
const SOURCE_SHA = process.env.REACT_APP_SOURCE_SHA || 'unknown';
try {
  const buildIdentity = window as unknown as Record<string, string>;
  buildIdentity.__MSAPLING_BUILD__ = BUILD_TAG;
  buildIdentity.__MSAPLING_SOURCE_SHA__ = SOURCE_SHA;
  // eslint-disable-next-line no-console
  console.info(`%cMSapling build: ${BUILD_TAG}`, 'color:#60a5fa;font-weight:600');
} catch {
  /* non-browser environment — ignore */
}

const EXTENSION_ASYNC_CHANNEL_CLOSED =
  'A listener indicated an asynchronous response by returning true, but the message channel closed before a response was received';

// Frontend error monitoring. Gated on REACT_APP_SENTRY_DSN: when unset (dev, or
// before the DSN is configured) this is a COMPLETE no-op, so it cannot break the
// app. Set the frontend Sentry project's DSN in the build env to turn it on — this
// is what makes browser errors (429 storms, API failures, render bugs) visible in
// Sentry, which today only sees unhandled BACKEND exceptions.
// Frontend Sentry project DSN. A browser DSN is PUBLIC by design (it ships in the
// bundle and only permits sending events to the project), so a hardcoded default is
// fine; the build-env var still overrides it if set.
const _SENTRY_DSN = process.env.REACT_APP_SENTRY_DSN
  || 'https://57a59414a884364950b2e0929374b20e@o4511613196173312.ingest.us.sentry.io/4511630737539072';
initializeSentry(_SENTRY_DSN, readPersistedOptOut());

// Ad conversion tracking (GA4 / Google Ads / Reddit Pixel). Each loader is
// gated on its own REACT_APP_* env var and is a complete no-op when unset —
// same pattern as the Sentry gating above. Real GA4/Google Ads IDs are not
// yet issued (separate account setup); the Reddit Pixel ID that already
// exists (a2_ioawm5d4plrd) belongs to a different product/domain
// (play.mtreeai.com) and is deliberately NOT wired in here — set
// REACT_APP_REDDIT_PIXEL_ID to msapling.com's own pixel ID once one exists.
initAdTracking();

// Ignore known browser-extension unhandled rejections that are not caused by app code.
window.addEventListener('unhandledrejection', (event) => {
  const reason = event?.reason as any;
  const message =
    typeof reason === 'string'
      ? reason
      : typeof reason?.message === 'string'
      ? reason.message
      : '';
  if (message.includes(EXTENSION_ASYNC_CHANNEL_CLOSED)) {
    event.preventDefault();
  }
});

// Apply stored theme synchronously before React renders to prevent FOUC.
// Reads the same key SettingsModal writes.
// When no preference is stored, honour the OS colour-scheme setting (020).
try {
  const stored = localStorage.getItem(LocalStorageKey.APPEARANCE_THEME);
  let effective: string;
  if (stored === 'light' || stored === 'dark') {
    effective = stored;
  } else if (stored === 'system' || stored === null || stored === undefined) {
    // No explicit preference — follow OS setting (default was always 'dark', but
    // that ignored light-mode users on first visit; card 020 fixes this).
    effective = window.matchMedia('(prefers-color-scheme: light)').matches ? 'light' : 'dark';
  } else {
    effective = 'dark';
  }
  document.documentElement.setAttribute('data-theme', effective);
} catch { /* localStorage unavailable */ }

// Set data-print-date on body just before printing so print.css header renders correctly.
window.addEventListener('beforeprint', () => {
  document.body.setAttribute('data-print-date', new Date().toLocaleString());
});

const rootElement = document.getElementById('root');
if (!rootElement) throw new Error('Failed to find the root element');

const root = ReactDOM.createRoot(rootElement as HTMLElement);
root.render(
  <React.StrictMode>
    <App />
  </React.StrictMode>
);

initBrowserErrorTelemetry();

emitFrontendEvent({ event_name: 'page_load', component: 'App', action: 'mount' });

reportWebVitals(({ name, value, id }) => {
  void postWebVital(name, value, id, window.location.pathname);
});
